import { ATENCION_BOT_BASE_URL } from '../config/atencionBot';

function ensureBaseUrl() {
  if (!ATENCION_BOT_BASE_URL) {
    throw new Error(
      'Bot de atención no configurado. Definí REACT_APP_ATENCION_BOT_URL o usá npm start con el proxy /atencion-bot.'
    );
  }
  return ATENCION_BOT_BASE_URL;
}

async function parseJsonResponse(res) {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail = data?.detail;
    let message = `Error ${res.status}`;
    if (typeof detail === 'string') {
      message = detail;
    } else if (Array.isArray(detail) && detail[0]?.msg) {
      message = detail[0].msg;
    }
    throw new Error(message);
  }
  return data;
}

async function botFetch(url, options = {}) {
  const { timeoutMs, ...fetchOptions } = options;
  let controller;
  let timeoutId;
  if (timeoutMs && typeof AbortController !== 'undefined') {
    controller = new AbortController();
    timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    fetchOptions.signal = controller.signal;
  }
  try {
    return await fetch(url, fetchOptions);
  } catch (err) {
    if (err?.name === 'AbortError') {
      throw new Error('La solicitud tardó demasiado. Probá de nuevo o escribí el medicamento.');
    }
    if (err instanceof TypeError) {
      throw new Error(
        'No se pudo conectar al bot. Verificá que uvicorn esté en :2000 y Redis en :6379.'
      );
    }
    throw err;
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}

export async function fetchAtencionBotHealth() {
  const base = ensureBaseUrl();
  const res = await botFetch(`${base}/health`);
  const data = await parseJsonResponse(res);
  if (data?.service !== 'botrecetasatencion') {
    throw new Error('El bot de atención no respondió correctamente.');
  }
  if (!data?.redis) {
    throw new Error(
      'Redis no está disponible (puerto 6379). En botrecetasatencion: docker compose up redis -d'
    );
  }
  return data;
}

export async function sendAtencionBotAudio({
  sessionId,
  phone,
  filename,
  mimeType,
  dataBase64
}) {
  const base = ensureBaseUrl();
  const res = await botFetch(`${base}/admin/simulate-audio`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    timeoutMs: 90000,
    body: JSON.stringify({
      session_id: sessionId,
      phone: phone || '+5490000000001',
      filename: filename || 'audio.ogg',
      mime_type: mimeType || 'audio/ogg',
      data_base64: dataBase64
    })
  });
  return parseJsonResponse(res);
}

export async function sendAtencionBotAttachment({
  sessionId,
  phone,
  filename,
  mimeType,
  dataBase64,
  caption = ''
}) {
  const base = ensureBaseUrl();
  const res = await botFetch(`${base}/admin/simulate-attachment`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    timeoutMs: 120000,
    body: JSON.stringify({
      session_id: sessionId,
      phone: phone || '+5490000000001',
      filename: filename || 'receta',
      mime_type: mimeType || 'image/jpeg',
      data_base64: dataBase64,
      caption: caption || ''
    })
  });
  return parseJsonResponse(res);
}

export async function sendAtencionSimMessage({ message, sessionId, phone }) {
  const base = ensureBaseUrl();
  const res = await botFetch(`${base}/admin/simulate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      message,
      session_id: sessionId,
      phone: phone || '+5490000000001'
    })
  });
  return parseJsonResponse(res);
}

export async function fetchAtencionBotCatalogSession(token) {
  const base = ensureBaseUrl();
  const res = await botFetch(`${base}/admin/catalog/${encodeURIComponent(token)}`);
  return parseJsonResponse(res);
}

export async function createAtencionBotCatalogToken(conversationId, phone, hits = []) {
  const base = ensureBaseUrl();
  const convId = String(conversationId || '').trim();
  if (!convId) throw new Error('conversation_id requerido');
  const res = await botFetch(
    `${base}/admin/conversations/${encodeURIComponent(convId)}/catalog-token`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        phone: phone || '+5490000000001',
        hits: Array.isArray(hits) ? hits : []
      })
    }
  );
  return parseJsonResponse(res);
}

export async function applyAtencionBotCatalogSelections(conversationId, phone, selections) {
  const base = ensureBaseUrl();
  const convId = String(conversationId || '').trim();
  if (!convId) throw new Error('conversation_id requerido');
  const res = await botFetch(
    `${base}/admin/conversations/${encodeURIComponent(convId)}/catalog-selections`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        phone: phone || '+5490000000001',
        selections: Array.isArray(selections) ? selections : []
      })
    }
  );
  return parseJsonResponse(res);
}

export async function checkoutAtencionBotCatalog(token, selections) {
  const base = ensureBaseUrl();
  const res = await botFetch(`${base}/admin/catalog/${encodeURIComponent(token)}/checkout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ selections })
  });
  return parseJsonResponse(res);
}

export async function fetchAtencionBotFrequentProducts(
  phone,
  { conversationId = '', dni = '', quick = false } = {}
) {
  const base = ensureBaseUrl();
  const params = new URLSearchParams();
  const rawPhone = String(phone || '').trim();
  if (rawPhone) params.set('phone', rawPhone);
  if (conversationId) params.set('conversation_id', String(conversationId));
  const rawDni = String(dni || '').replace(/\D/g, '');
  if (rawDni.length >= 7) params.set('dni', rawDni);
  if (quick) params.set('quick', '1');
  const res = await botFetch(`${base}/admin/cliente-farmacia-frequent?${params}`);
  return parseJsonResponse(res);
}

export async function fetchAtencionBotProductos(busqueda, { limit = 20 } = {}) {
  const base = ensureBaseUrl();
  const q = String(busqueda || '').trim();
  const params = new URLSearchParams({ limit: String(limit) });
  if (q) params.set('busqueda', q);
  const res = await botFetch(`${base}/admin/productos?${params}`);
  return parseJsonResponse(res);
}

export async function clearAtencionSimSession(sessionId) {
  const base = ensureBaseUrl();
  const convId = sessionId.startsWith('local-') ? sessionId : `local-${sessionId}`;
  const res = await botFetch(`${base}/admin/sessions/${encodeURIComponent(convId)}/clear`, {
    method: 'POST'
  });
  return parseJsonResponse(res);
}
